import { ImageBaseIconRaw } from '@tableau/icons/ImageBaseIcon/raw';
import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { Messages } from '../../../gen-src/Localize';
import { encodeSvg } from '../../icons/Icons';
import { makeTestId } from '../../utils/Declarative';
import { generateId } from '../../utils/DomUtil';
import { TextAreaAttrs } from '../../utils/HtmlAttrs';
import { PropsAndElementAttrs } from '../../utils/ReactHelpers';
import { ExpandoWidget } from '../Expando/ExpandoWidget';
import { TextAreaProps, TextAreaWidget } from '../TextAreaWidget';
import { TextFormatterHeight } from './TextFormatterDisplayWidget';

export interface TextFormatterEditorProps extends Pick<TextAreaProps, 'valid' | 'message'> {
  disabled?: boolean;
  /** Supportive text to display in a tooltip for when the toolbar is disabled */
  disabledTooltipText?: string;
  /** Markup to display in the editor */
  markup: string;
  /**
   * Label to display above the field.
   * You must provide a label to be WCAG-compliant.
   */
  label: React.ReactNode;
  /** If supplied, will be applied to the root div, whereas oll other HTMLAttributes will be applied to the textarea */
  style?: React.CSSProperties;
  /** Adds suffix '-TextFormatterEditorWidget' */
  testId: string;
}

const TextAreaStyleName = TypeStyle.style({
  display: 'flex',
  $nest: {
    '> textarea': {
      height: TextFormatterHeight,
      // Safari defaults to a 2px left/right margin on textarea, which causes a jump when switching modes
      margin: 0,
      resize: 'vertical',
    },
  }
});

const EditorStyleName = TypeStyle.style({
  position: 'relative',
});

export const TextFormatterEditorWidget: React.FC<PropsAndElementAttrs<TextFormatterEditorProps, TextAreaAttrs>> = props => {
  const { className, disabled, disabledTooltipText, id, label, markup, message, onChange, onKeyDown, onSelect, style, testId, valid, ...htmlAttrs } = props;
  // const [activeFormatters, setActiveFormatters] = React.useState<Array<FormatterType>>([]);
  // const [focusWithin, setFocusWithin] = React.useState(false);
  const [textAreaId] = React.useState<string>(id || generateId());
  const textAreaRef = React.useRef<HTMLTextAreaElement>(null);

  const handleChange = React.useCallback<React.ChangeEventHandler<HTMLTextAreaElement>>(e => {
    if (e.target.value !== markup) {
      // setActiveFormatters(getActiveFormatters(textAreaRef.current));
    }
    onChange?.(e);
  }, [markup, onChange]);

  const handleKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // if (isCtrl(e) && ShortcutKeys.indexOf(e.keyCode) !== -1) {
    //   const formatterType: (FormatterType | undefined) = getFormatterTypeFromShortcut(e);
    //   if (formatterType) {
    //     applyMarkup(formatterType, textAreaRef.current!);
    //     e.stopPropagation();
    //     e.preventDefault();
    //   }
    // }

    onKeyDown?.(e);
  }, [onKeyDown]);

  const handleSelect = React.useCallback((e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    // setActiveFormatters(getActiveFormatters(textAreaRef.current));
    onSelect?.(e);
  }, [onSelect]);

  const handleFocusBlur = React.useCallback<React.FocusEventHandler<HTMLElement>>(() => {
    // setFocusWithin(e.type === 'focus' || e.currentTarget.contains(findBlurRelatedTarget(e)));
  }, []);

  return (
    <div
      className={EditorStyleName}
      data-tb-test-id={makeTestId(testId, '-TextFormatterEditorWidget')}
      onBlurCapture={handleFocusBlur}
      onFocusCapture={handleFocusBlur}
      style={style}
    >
      {label && <label htmlFor={textAreaId}>{label}</label>}
      {/* <Tooltip
        getContent={() => disabled && disabledTooltipText ? <span>{disabledTooltipText}</span> : null}
        followCursor={true}
        position={TooltipPosition.Top}
      >
        <TextFormatterToolbar
          activeFormatters={focusWithin ? activeFormatters : []}
          disabled={!!disabled}
          textAreaId={textAreaId}
          textAreaElement={textAreaRef.current}
        />
      </Tooltip> */}
      <TextAreaWidget
        {...htmlAttrs}
        className={TypeStyle.classes(className, TextAreaStyleName)}
        value={markup}
        disabled={disabled}
        id={textAreaId}
        label={null} // null because we render a label separately above, so that it appears above the toolbar
        message={message}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onSelect={handleSelect}
        testId={testId}
        ref={textAreaRef}
        valid={valid}
      />
      {!disabled && <ExpandoWidget
        label={Messages.formattingGuide()}
      >
        {FormattingHintsTable()}
      </ExpandoWidget>}
    </div>
  );
};
TextFormatterEditorWidget.displayName = 'TextFormatterEditorWidget';

// the following is an interpreted-dance reimagining of FormattingHints.tsx from vizportalclient
const borderStyle = '1px solid #c9c9c9'; // elevation/border/on-light/default
const FormattingHeaderStyleName = TypeStyle.style({
  borderTop: borderStyle,
  borderLeft: borderStyle,
  padding: 6,
  $nest: { '& ~ *': { padding: 6 } },
});
const FormattingLeftColumnStyleName = TypeStyle.style({
  borderLeft: borderStyle,
  borderTop: borderStyle,
});
const FormattingRightColumnStyleName = TypeStyle.style({
  borderLeft: borderStyle,
  borderRight: borderStyle,
  borderTop: borderStyle,
});
const FormattingHintsTable = () =>
  <div style={{ display: 'inline-flex', whiteSpace: 'nowrap' }}>
    <div>
      <div className={FormattingHeaderStyleName} style={{borderLeft: borderStyle}}>{Messages.formattingHintsColumnExample()}</div>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <div className={FormattingLeftColumnStyleName}><a href='#' onClick={e => e.preventDefault()}>{Messages.formattingHintsLink()}</a></div>
      <div className={FormattingLeftColumnStyleName}><strong>{Messages.formattingHintsBold()}</strong></div>
      <div className={FormattingLeftColumnStyleName}><em>{Messages.formattingHintsItalics()}</em></div>
      <div className={FormattingLeftColumnStyleName}><ins>{Messages.formattingHintsUnderline()}</ins></div>
      <div className={FormattingLeftColumnStyleName} style={{borderBottom: borderStyle}}>
        <img alt='' src={encodeSvg(ImageHintSvg)} style={{ width: 19, height: 19, verticalAlign: 'middle' }} />
        {Messages.formattingHintsImage()}
      </div>
    </div>
    <div>
      <div className={FormattingHeaderStyleName} style={{borderRight: borderStyle}}>{Messages.formattingHintsColumnMarkup()}</div>
      <div className={FormattingRightColumnStyleName}>{`"${Messages.formattingHintsLink()}":http://www.example.com`}</div>
      <div className={FormattingRightColumnStyleName}>{`*${Messages.formattingHintsBold()}*`}</div>
      <div className={FormattingRightColumnStyleName}>{`_${Messages.formattingHintsItalics()}_`}</div>
      <div className={FormattingRightColumnStyleName}>{`+${Messages.formattingHintsUnderline()}+`}</div>
      <div className={FormattingRightColumnStyleName} style={{borderBottom: borderStyle, height: 19}}>{'!http://www.example.com/image.png!'}</div>
    </div>
  </div>;

const ImageHintSvg = ImageBaseIconRaw(20, '#666');
